import routes from './routes';
import locales from './locales';

/*
 * Every page title, in one place.
 *
 * These used to live in an if/else chain inside NavigationLayout, which meant
 * adding a section without touching that chain silently fell through to
 * "Dashboard", as cargo claims did. Modules that printed their own heading
 * then disagreed with the page head above them.
 *
 * Rules are evaluated in order, so put the more specific path first. `test`
 * receives the pathname and the route params the layout already has.
 */

export interface PageTitleContext {
  pathname: string;
  // Present on the ticket detail routes of List 1 and List 2.
  ticketId?: string;
  // Present on a cargo claim detail route.
  reference?: string;
}

interface PageTitleRule {
  test: (context: PageTitleContext) => boolean;
  title: (context: PageTitleContext) => string;
  // A line under the title saying where the page's data comes from.
  description?: string;
  // The page's previous title, kept in small type under the new one so people
  // who know the lists by number still find their way.
  subtitle?: string | ((context: PageTitleContext) => string);
}

const at = (index: string) => (context: PageTitleContext) =>
  context.pathname.includes(index);

const rule = (
  test: (context: PageTitleContext) => boolean,
  title: string | ((context: PageTitleContext) => string),
  description?: string,
  subtitle?: PageTitleRule['subtitle']
): PageTitleRule => ({
  test,
  title: typeof title === 'string' ? () => title : title,
  description,
  subtitle,
});

// An endorsement page under the name the sidebar gives it, with its old title
// underneath.
const renamed = (
  test: (context: PageTitleContext) => boolean,
  title: string,
  previous: () => string
) => rule(test, title, undefined, previous);

const RULES: PageTitleRule[] = [
  // Docs. The tab strip on the page names the specific document, so the page
  // head stays put instead of repeating it a line above.
  rule(at(routes.docs.index), 'Documentation'),

  rule(
    (c) => c.pathname === routes.overview,
    'Overview',
    'Endorsement operations'
  ),

  // Claims
  rule((c) => at(routes.cargoClaims.index)(c) && !!c.reference, 'Claim Details'),
  rule(at(routes.cargoClaims.index), 'Cargo Claims'),
  rule(
    (c) => at(routes.healthClaims.index)(c) && at(routes.healthClaims.dashboard)(c),
    'Dashboard - GlobeMed'
  ),
  rule(
    (c) => at(routes.healthClaims.index)(c) && at(routes.healthClaims.closedTickets)(c),
    'Closed Tickets',
    'GlobeMed - CLOSED status'
  ),
  rule(
    (c) =>
      at(routes.healthClaims.index)(c) &&
      at(routes.healthClaims.reimbursementApproval)(c),
    'Approvals',
    'Global - Approval table'
  ),
  rule(
    (c) =>
      at(routes.healthClaims.index)(c) &&
      at(`${routes.healthClaims.reimbursementClaims}/${routes.healthClaims.reimbursementAzadea}`)(c),
    'AZADEA Reimbursement Claims'
  ),
  rule(
    (c) =>
      at(routes.healthClaims.index)(c) &&
      at(`${routes.healthClaims.reimbursementClaims}/${routes.healthClaims.reimbursementGlobal}`)(c),
    'GLOBAL Reimbursement Claims'
  ),
  rule(
    (c) =>
      at(routes.healthClaims.index)(c) &&
      at(routes.healthClaims.reimbursementClaims)(c),
    'Reimbursement Claims'
  ),
  rule(
    (c) => at(routes.healthClaims.index)(c) && at(routes.healthClaims.directBilling)(c),
    'Direct Billing Claims'
  ),
  rule(at(routes.healthClaims.index), 'Health Claims'),

  // Endorsement
  rule(
    (c) => at(routes.listZero.index)(c) && at(routes.listZero.participants)(c),
    'Participants'
  ),
  renamed(at(routes.listZero.index), 'Source Data', () => 'List 0 History'),

  rule(
    (c) => at(routes.listOne.index)(c) && !!c.ticketId,
    () => locales.azadea.ticketDetails.title
  ),
  renamed(
    at(routes.listOne.index),
    'Pending Requests',
    () => `${locales.general.list} One Tickets`
  ),

  rule(
    (c) => at(routes.listTwo.index)(c) && !!c.ticketId,
    () => locales.azadea.ticketDetails.title
  ),
  renamed(
    at(routes.listTwo.index),
    'In Progress',
    () => `${locales.general.list} Two Tickets`
  ),

  renamed(
    at(routes.listThree.index),
    'Insured Principals',
    () => `${locales.general.list} Three Table`
  ),
  renamed(
    at(routes.listFour.index),
    'Closed Endorsements',
    () => `${locales.general.list} Four Tickets`
  ),
  renamed(
    at(routes.listFive.index),
    'Invoices',
    () => `${locales.general.list} Five Tickets`
  ),
  renamed(
    at(routes.listSix.index),
    'Invoice Line Items',
    () => `${locales.general.list} Six Tickets`
  ),

  renamed(
    at(routes.logs.index),
    'Activity Logs',
    () => locales.azadea.historyTickets.title
  ),
];

const FALLBACK: PageTitleRule = rule(() => true, 'Overview', undefined, () =>
  'Dashboard'
);

function resolveRule(context: PageTitleContext): PageTitleRule {
  return RULES.find((entry) => entry.test(context)) ?? FALLBACK;
}

export function resolvePageTitle(context: PageTitleContext): string {
  return resolveRule(context).title(context);
}

export function resolvePageDescription(
  context: PageTitleContext
): string | undefined {
  return resolveRule(context).description;
}

export function resolvePageSubtitle(
  context: PageTitleContext
): string | undefined {
  const { subtitle } = resolveRule(context);
  return typeof subtitle === 'function' ? subtitle(context) : subtitle;
}
